import { assertSafeStructure, payloadHash } from "./canonical";
import { detectFormat, formatLabel, toIsoTimestamp } from "./detect";
import { legacyToClinicalOSBackup, type LegacyClinicalOSBackup } from "./legacy";
import { piruToClinicalOSBackup, previewPiru, type PiruBackup, type PiruPreview } from "./piru";
import { BACKUP_FORMAT_VERSION, specFor } from "./schema";
import type { ClinicalOSBackupFile, DetectedFormat } from "./types";

export type ChecksumStatus = "valid" | "invalid" | "missing" | "not-applicable";

export interface BackupInspection {
  format: DetectedFormat;
  label: string;
  appVersion: string | null;
  exportedAt: string | null;
  counts: Record<string, number>;
  totalRows: number;
  warnings: string[];
  errors: string[];
  checksumStatus: ChecksumStatus;
  requiresPatient: boolean;
  raw: unknown;
  piruPreview?: PiruPreview;
}

function tableCounts(tables: unknown): Record<string, number> {
  if (!tables || typeof tables !== "object" || Array.isArray(tables)) return {};
  const out: Record<string, number> = {};
  for (const [table, value] of Object.entries(tables as Record<string, unknown>)) {
    if (Array.isArray(value)) out[table] = value.length;
  }
  return out;
}

function countTotal(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, value) => sum + (Number.isFinite(value) ? value : 0), 0);
}

export async function inspectBackup(raw: unknown): Promise<BackupInspection> {
  const warnings: string[] = [];
  const errors: string[] = [];
  try {
    assertSafeStructure(raw);
  } catch (error: any) {
    return {
      format: "unknown",
      label: "Arquivo rejeitado",
      appVersion: null,
      exportedAt: null,
      counts: {},
      totalRows: 0,
      warnings,
      errors: [error?.message ?? "Estrutura inválida."],
      checksumStatus: "not-applicable",
      requiresPatient: false,
      raw,
    };
  }

  const format = detectFormat(raw);
  if (format === "unknown") {
    errors.push("Formato não reconhecido. Aceitos: ClinicalOS Backup v1, backup antigo ClinicalOS e Piru Export v1.");
    return {
      format,
      label: formatLabel(format),
      appVersion: null,
      exportedAt: null,
      counts: {},
      totalRows: 0,
      warnings,
      errors,
      checksumStatus: "not-applicable",
      requiresPatient: false,
      raw,
    };
  }

  if (format === "piru") {
    const piru = raw as PiruBackup;
    if (piru.piruExportVersion !== 1) errors.push(`Versão Piru não suportada: ${piru.piruExportVersion ?? "ausente"}.`);
    const piruPreview = previewPiru(piru);
    warnings.push(...piruPreview.warnings);
    const counts = {
      clinical_sessions: piruPreview.sessions,
      medication_dose_logs: piruPreview.doses,
      patient_psylog_events: piruPreview.notes + piruPreview.doses,
      medication_inventory: piruPreview.inventoryItems,
      medication_inventory_movements: piruPreview.inventoryMovements,
      substances_custom: piruPreview.customSubstances,
    };
    return {
      format,
      label: `Piru Export v${piru.piruExportVersion ?? "?"}`,
      appVersion: piruPreview.appVersion,
      exportedAt: piruPreview.exportedAt,
      counts,
      totalRows: countTotal(counts),
      warnings,
      errors,
      checksumStatus: "not-applicable",
      requiresPatient: true,
      raw,
      piruPreview,
    };
  }

  if (format === "legacy") {
    const converted = legacyToClinicalOSBackup(raw as LegacyClinicalOSBackup);
    const counts = tableCounts(converted.data.tables);
    warnings.push("Backup antigo detectado: será convertido para ClinicalOS Backup v1 antes da restauração.");
    warnings.push("Canônicos globais do arquivo antigo não serão sobrescritos; serão apenas re-resolvidos por referência.");
    return {
      format,
      label: formatLabel(format),
      appVersion: converted.appVersion ?? null,
      exportedAt: toIsoTimestamp(converted.exportedAt),
      counts,
      totalRows: countTotal(counts),
      warnings,
      errors,
      checksumStatus: "missing",
      requiresPatient: false,
      raw,
    };
  }

  const file = raw as ClinicalOSBackupFile;
  if (file.formatVersion !== BACKUP_FORMAT_VERSION) {
    errors.push(`Versão do backup não suportada: ${file.formatVersion}. Esta versão do app lê o formato ${BACKUP_FORMAT_VERSION}.`);
  }
  if (file.app !== "ClinicalOS") warnings.push(`Origem declarada: ${String(file.app ?? "desconhecida")}.`);
  if (!file.data || typeof file.data !== "object" || !file.data.tables || typeof file.data.tables !== "object") {
    errors.push("Backup ClinicalOS sem data.tables válido.");
  }

  const counts = tableCounts(file.data?.tables);
  for (const table of Object.keys(counts)) {
    if (!specFor(table)) warnings.push(`Tabela desconhecida "${table}" será ignorada.`);
  }
  if (file.counts) {
    for (const [table, count] of Object.entries(counts)) {
      if (typeof file.counts[table] === "number" && file.counts[table] !== count) {
        warnings.push(`Contagem declarada de ${table} (${file.counts[table]}) difere do conteúdo (${count}).`);
      }
    }
  }

  let checksumStatus: ChecksumStatus = "missing";
  if (file.integrity?.payloadHash) {
    const actual = await payloadHash(file.data);
    if (actual && actual === file.integrity.payloadHash) checksumStatus = "valid";
    else {
      checksumStatus = "invalid";
      errors.push("Checksum SHA-256 inválido: o conteúdo não corresponde ao hash gravado no backup.");
    }
  } else {
    warnings.push("Arquivo sem checksum verificável. A restauração ainda pode ser feita, mas a integridade não é autenticada.");
  }

  return {
    format,
    label: formatLabel(format),
    appVersion: file.appVersion ?? null,
    exportedAt: toIsoTimestamp(file.exportedAt),
    counts,
    totalRows: countTotal(counts),
    warnings,
    errors,
    checksumStatus,
    requiresPatient: false,
    raw,
  };
}

export function prepareBackupForRestore(
  inspection: BackupInspection,
  userId: string,
  patientId?: string,
): ClinicalOSBackupFile {
  if (inspection.errors.length) throw new Error(inspection.errors.join(" "));
  if (inspection.format === "clinicalos") return inspection.raw as ClinicalOSBackupFile;
  if (inspection.format === "legacy") return legacyToClinicalOSBackup(inspection.raw as LegacyClinicalOSBackup);
  if (inspection.format === "piru") {
    if (!patientId) throw new Error("Selecione o paciente de destino para o backup Piru.");
    return piruToClinicalOSBackup(inspection.raw as PiruBackup, patientId, userId);
  }
  throw new Error("Formato de backup não suportado.");
}
